"use client";

import cn from "clsx";
import { useTranslations } from "next-intl";
import { FaSpinner } from "react-icons/fa";

import { ProductPage } from "@/clients/contentful";
import Button from "@/common/components/button";
import { useCartLimit } from "@/common/hooks/use-cart-limit";
import { formatDateCompact } from "@/common/hooks/use-date";
import { config } from "@/config";
import { useStoreProduct } from "@/features/product/contexts/product-context";
import { productTestIds } from "../test-ids";
import { AddToCartButton, AddToCartButtonChildrenProps } from "./add-to-cart-button";
import { AmendOrderButton, AmendOrderButtonChildrenProps } from "./amend-order-button";
import { useProductDispatchInfo } from "./hooks/use-product-dispatch-info";
import { PriceInfo } from "./price-info";
import { UpdateCartItemButton, UpdateCartItemButtonChildrenProps } from "./update-cart-item-button";

const DispatchInfo = ({
  deliveryDate,
  cutOffDate,
}: {
  deliveryDate?: string;
  cutOffDate?: string;
}) => {
  const t = useTranslations("common");
  const { orderCompletionTime } = useProductDispatchInfo({ cutOffDate });

  if (!deliveryDate || !cutOffDate) {
    return (
      <div className="flex items-center font-normal text-white">
        <FaSpinner className="mr-2 inline-block animate-spin text-xl" />
        {t("text-calculating-delivery-date")}
      </div>
    );
  }

  return (
    <div className="items-center font-normal text-white lg:flex">
      <p className="2xl:text-xl">
        {t.rich("text-order-artworks-dispatch-info", {
          highlight: (text: React.ReactNode) => <span className="font-semibold">{text}</span>,
          time: orderCompletionTime,
          date: formatDateCompact(deliveryDate),
          delivery: t("text-free-uk-delivery"),
        })}
      </p>
    </div>
  );
};

export function StickyPriceBar({ product }: { product: ProductPage }) {
  const t = useTranslations();
  const cartLimitReached = useCartLimit();

  const { isError, isConfigValid, deliveryOption, updateLineItemId, updateOrderId } =
    useStoreProduct();

  return (
    <>
      <div className="bg-grey-100 fixed z-[15] mx-auto w-full">
        <div className="mx-auto max-w-[1920px] px-4 lg:px-8 2xl:px-16">
          <div className="h-16 grid-cols-9 items-center gap-x-10 lg:grid lg:h-[72px] xl:gap-x-14">
            <div className="col-span-5 mr-3 hidden items-center lg:flex">
              <DispatchInfo
                deliveryDate={deliveryOption?.deliveryDate}
                cutOffDate={deliveryOption?.cutOffDate}
              />
            </div>
            <div className="flex h-full items-center justify-between text-sm lg:col-span-4">
              <PriceInfo
                className="!text-white"
                theme="dark"
                showError={false}
                testId={productTestIds.productStickyBarPrice}
                showLoyaltyTier
              />
              <div>
                {!updateOrderId && !updateLineItemId && (
                  <AddToCartButton product={product}>
                    {({ isLoading, handleAddToBasket }: AddToCartButtonChildrenProps) => (
                      <Button
                        onClick={handleAddToBasket}
                        loading={isLoading}
                        disabled={isLoading || isError || !isConfigValid || cartLimitReached}
                        variant="success"
                        className={cn(
                          "h-10 w-40 !rounded-3xl py-2 text-base !font-semibold uppercase lg:h-12 lg:w-60 lg:text-2xl 2xl:w-80",
                          {
                            "lg:text-base !w-auto px-6 text-sm": cartLimitReached,
                          },
                        )}
                      >
                        {cartLimitReached
                          ? t.rich("common.text-basket-limit-reached", {
                              items: () => config.NEXT_PUBLIC_CART_LIMIT,
                            })
                          : t("common.text-add-to-cart")}
                      </Button>
                    )}
                  </AddToCartButton>
                )}
                {!updateOrderId && updateLineItemId && (
                  <UpdateCartItemButton sku={product.fields.sku!} lineItemId={updateLineItemId}>
                    {({ isLoading, handleUpdateCartItem }: UpdateCartItemButtonChildrenProps) => (
                      <Button
                        onClick={handleUpdateCartItem}
                        loading={isLoading}
                        disabled={isLoading || isError || !isConfigValid}
                        variant="success"
                        className="h-10 w-40 !rounded-3xl py-2 text-base !font-semibold uppercase lg:h-12 lg:w-60 lg:text-2xl 2xl:w-80"
                      >
                        {t("common.button-update-item")}
                      </Button>
                    )}
                  </UpdateCartItemButton>
                )}
                {updateOrderId && updateLineItemId && (
                  <AmendOrderButton
                    orderId={updateOrderId}
                    lineItemId={updateLineItemId}
                    productSku={product.fields.sku!}
                  >
                    {({ isLoading, handleAmendOrderLineItem }: AmendOrderButtonChildrenProps) => (
                      <Button
                        onClick={handleAmendOrderLineItem}
                        loading={isLoading}
                        disabled={isLoading || isError || !isConfigValid}
                        variant="success"
                        className="h-10 w-40 !rounded-3xl py-2 text-base !font-semibold uppercase lg:h-12 lg:w-60 lg:text-2xl 2xl:w-80"
                      >
                        {t("common.button-amend-order-item")}
                      </Button>
                    )}
                  </AmendOrderButton>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="h-16 lg:h-20"></div>
    </>
  );
}
